class SiteHeader extends HTMLElement {
  static scrollThreshold = 8;
  static themeColorLight = '#ffffff';
  static themeColorDark = '#1f2327';

  constructor() {
    super();

    this.searchToggles = this.querySelectorAll('[data-search-toggle]');
    this.searchDrawer = this.querySelector('.header__search-drawer');
    this.searchDrawerBackground = this.querySelector('[data-search-drawer-background]');
  }

  connectedCallback() {
    this.searchToggles.forEach((toggle) => {
      toggle.addEventListener('click', this.handleSearchToggleClick);
    });
    this.searchDrawerBackground?.addEventListener('click', this.handleSearchDrawerBackgroundClick);
    document.addEventListener('keydown', this.handleSearchKeydown);

    this.measureAnnouncementBar();
    window.addEventListener('resize', this.measureAnnouncementBar);

    this.updateHeaderOffset();
    window.addEventListener('resize', this.updateHeaderOffset);

    this.lastScrollY = window.scrollY;
    this.ticking = false;
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // The header's `top` depends on --announcement-bar-height, which isn't
    // known until the measurement above runs — reveal only now, instead of
    // showing at the wrong (fallback) position and visibly jumping once
    // corrected.
    this.classList.add('header--ready');
  }

  disconnectedCallback() {
    this.searchToggles.forEach((toggle) => {
      toggle.removeEventListener('click', this.handleSearchToggleClick);
    });
    this.searchDrawerBackground?.removeEventListener('click', this.handleSearchDrawerBackgroundClick);
    document.removeEventListener('keydown', this.handleSearchKeydown);
    window.removeEventListener('resize', this.measureAnnouncementBar);
    window.removeEventListener('resize', this.updateHeaderOffset);
    window.removeEventListener('scroll', this.handleScroll);
  }

  measureAnnouncementBar = () => {
    const announcementBar = document.querySelector('.announcement-bar');
    this.announcementBarHeight = announcementBar?.offsetHeight ?? 0;
    this.updateAnnouncementBarOffset(window.scrollY);
    this.updateThemeColor();
  };

  updateAnnouncementBarOffset(scrollY) {
    const offset = Math.max(0, (this.announcementBarHeight ?? 0) - scrollY);
    // Set globally (not just on `this`) so other components — e.g. the
    // product page's sticky info column — can account for the space the
    // announcement bar currently occupies via the same custom property.
    document.documentElement.style.setProperty('--announcement-bar-height', `${offset}px`);
  }

  // Set globally so other fixed/sticky elements — e.g. the sticky nav —
  // can offset themselves below the header's *current* visible height,
  // which collapses to 0 while `.header--hidden` is applied (a transform,
  // so `offsetHeight` alone wouldn't reflect it).
  updateHeaderOffset = () => {
    const isHidden = this.classList.contains('header--hidden');
    const height = isHidden ? 0 : this.offsetHeight;
    document.documentElement.style.setProperty('--header-offset', `${height}px`);
  };

  updateThemeColor() {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;

    const hasAnnouncementBar = (this.announcementBarHeight ?? 0) > 0;
    meta.setAttribute('content', hasAnnouncementBar ? SiteHeader.themeColorDark : SiteHeader.themeColorLight);
  }

  handleSearchToggleClick = (event) => {
    if (this.searchDrawer?.classList.contains('is-open')) {
      this.closeSearchDrawer();
    } else {
      this.openSearchDrawer(event.currentTarget);
    }
  };

  handleSearchKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeSearchDrawer({ focusToggle: true });
    }
  };

  handleSearchDrawerBackgroundClick = () => {
    this.closeSearchDrawer();
  };

  openSearchDrawer(trigger) {
    if (!this.searchDrawer) return;

    const headerMenu = this.querySelector('header-menu');
    headerMenu?.closeMobileDrawer();
    headerMenu?.closeSubmenus();

    this.activeSearchToggle = trigger ?? null;
    this.searchToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
    this.searchDrawer.classList.add('is-open');
    this.searchDrawerBackground?.classList.add('is-open');
    this.classList.remove('header--hidden');
    this.updateHeaderOffset();
    document.documentElement.classList.add('scroll-locked');
    this.searchDrawer.querySelector('input[type="search"]')?.focus();
  }

  closeSearchDrawer({ focusToggle = false } = {}) {
    if (!this.searchDrawer?.classList.contains('is-open')) return;

    this.searchToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    this.searchDrawer.classList.remove('is-open');
    this.searchDrawerBackground?.classList.remove('is-open');
    document.documentElement.classList.remove('scroll-locked');
    // Hides any predictive search results left open from before closing,
    // so they don't flash back into view stale/mismatched on next open.
    this.searchDrawer.querySelector('predictive-search')?.close();

    if (focusToggle) {
      (this.activeSearchToggle ?? this.searchToggles[0])?.focus();
    }
    this.activeSearchToggle = null;
  }

  handleScroll = () => {
    if (this.ticking) return;

    this.ticking = true;
    window.requestAnimationFrame(this.updateVisibility);
  };

  updateVisibility = () => {
    if (document.documentElement.classList.contains('scroll-locked')) {
      this.ticking = false;
      return;
    }

    const currentScrollY = Math.max(window.scrollY, 0);
    const delta = currentScrollY - this.lastScrollY;

    this.updateAnnouncementBarOffset(currentScrollY);

    if (currentScrollY <= this.offsetHeight) {
      this.classList.remove('header--hidden');
      this.classList.remove('header--solid');
    } else if (delta > SiteHeader.scrollThreshold) {
      this.classList.add('header--hidden');
    } else if (delta < -SiteHeader.scrollThreshold) {
      this.classList.remove('header--hidden');
      this.classList.add('header--solid');
    }

    this.updateHeaderOffset();

    this.lastScrollY = currentScrollY;
    this.ticking = false;
  };
}

customElements.define('site-header', SiteHeader);
